import React, { useEffect, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { ILeaseInvoice, ILeaseRecording } from 'src/models/dlcLease';
import { DateField, Field, SelectField } from './Master';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';
import RecordingDetailsDialog from './RecordingDetailsDialog';

// --- Constants ---

const INSTRUMENT_TYPE_OPTIONS = [
    { value: 'Y', label: 'Book/Page' },
    { value: 'L', label: 'Liber/Page' },
    { value: 'N', label: 'Source Doc' },
];

const RECORDING_STATUS_OPTIONS = [
    { value: '1', label: 'Neither' },
    { value: '2', label: 'To Be Transmitted' },
    { value: '3', label: 'To Be Assigned' },
];

interface IRecordingModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (updatedRecordings: ILeaseRecording[]) => Promise<boolean>;
    invoice?: ILeaseInvoice;
    isEditable: boolean;
    clNo?: number;
    prNo?: number;
    leaseNo?: number;
    error?: ILeaseRecordsApiError | null;
}

const RecordingsModal: React.FC<IRecordingModalProps> = ({ isOpen, onClose, onSave, invoice, isEditable, clNo, prNo, leaseNo, error }) => {
    const { get, post, response } = useFetch();

    const [recordings, setRecordings] = useState<Partial<ILeaseRecording>[]>([]);
    const [detailsIndex, setDetailsIndex] = useState<number | null>(null);
    const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());
    const [invalidRecordedDateIndices, setInvalidRecordedDateIndices] = useState<Set<number>>(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [statesList, setStatesList] = useState<{ value: string; label: string }[]>([]);
    const [isUpdatingKudu, setIsUpdatingKudu] = useState(false);
    const [kuduUpdateResult, setKuduUpdateResult] = useState<{ success: boolean; message: string } | null>(null);

    // Cache for county dropdowns (Key: State Code, Value: Array of Select Options)
    const [countiesMap, setCountiesMap] = useState<Record<string, { value: string; label: string }[]>>({});

    useEffect(() => {
        const loadStates = async (): Promise<void> => {
            const res = await get('/v1.0/leases/states');
            if (response.ok && Array.isArray(res)) {
                setStatesList(res.map((s: { stCode: string; abbr: string }) => ({ value: s.stCode, label: s.abbr })));
            }
        };
        loadStates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCountiesForState = async (stateCode: string): Promise<void> => {
        if (!stateCode) {
            return;
        }
        // If we already fetched counties for this state, don't fetch again
        if (countiesMap[stateCode]) {
            return;
        }

        const res = await get(`/v1.0/leases/states/${stateCode}/counties`);
        if (response.ok && Array.isArray(res)) {
            setCountiesMap((prev) => ({
                ...prev,
                [stateCode]: res.map((r: { county: string }) => ({ value: r.county, label: r.county })),
            }));
        }
    };

    useEffect(() => {
        setDetailsIndex(null);
        if (isOpen && invoice) {
            const initialRecordings = invoice.recordings ? JSON.parse(JSON.stringify(invoice.recordings)) : [];
            setRecordings(initialRecordings);
            setExpandedIndices(initialRecordings.length > 0 ? new Set([0]) : new Set());
            setInvalidRecordedDateIndices(new Set());
            setIsDirty(false);
            setSaveStatus('idle');
            setKuduUpdateResult(null);

            // Pre-fetch counties for any states that already exist on loaded records
            initialRecordings.forEach((rec: Partial<ILeaseRecording>) => {
                if (rec.state) {
                    fetchCountiesForState(rec.state);
                }
            });
        } else {
            setRecordings([]);
            setExpandedIndices(new Set());
            setInvalidRecordedDateIndices(new Set());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice, isOpen]);

    if (!isOpen || !invoice) {
        return null;
    }

    // Recordings stay editable even after the invoice is posted -- the recording/
    // transmittal workflow happens post-posting. Invoice # and Check # are held
    // read-only per-field below.
    const canEditRecordings = isEditable;
    const isPosted = !!invoice.arDate;
    const canEditRecordingFinancials = canEditRecordings && !isPosted;
    const canRemoveRecordings = canEditRecordings && !isPosted;
    const invoiceNumber = invoice.invoiceNo1 ? invoice.invoiceNo1.toString() + (invoice.invoiceAdd || '') : 'New';

    const toggleRow = (index: number): void => {
        setExpandedIndices((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const formatDateForText = (value?: Date | string): string => {
        if (!value) {
            return '';
        }

        if (typeof value === 'string') {
            const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (isoMatch) {
                return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
            }
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value.toString();
        }

        return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
    };

    const normalizeManualDate = (value: string): string => {
        const trimmed = value.trim();
        const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (slashMatch) {
            const month = slashMatch[1].padStart(2, '0');
            const day = slashMatch[2].padStart(2, '0');
            return `${slashMatch[3]}-${month}-${day}`;
        }

        return trimmed;
    };

    const handleDateFieldChange = (index: number, field: keyof ILeaseRecording, value: string): void => {
        handleFieldChange(index, field, normalizeManualDate(value));
    };

    const handleFieldChange = (index: number, field: keyof ILeaseRecording, value: string | null): void => {
        const updated = [...recordings];
        const currentOp = updated[index].operation;

        const numericFields: (keyof ILeaseRecording)[] = ['recFee', 'checkNo', 'assignNo', 'transmitNo', 'recordingStatus'];
        const parsedValue = numericFields.includes(field) ? (value ? parseFloat(value) : null) : value;

        updated[index] = {
            ...updated[index],
            [field]: parsedValue,
            operation: currentOp === 'add' ? 'add' : 'update',
        };

        // If the State changed, we must clear the current County and fetch the new state's county list
        if (field === 'state' && value) {
            updated[index].county = ''; // Reset county since state changed
            fetchCountiesForState(value);
        } else if (field === 'state' && !value) {
            updated[index].county = ''; // Reset county if state is cleared
        }

        setRecordings(updated);
        setIsDirty(true);
        setSaveStatus('idle');
    };

    const handleAddRow = async (): Promise<void> => {
        const newIndex = recordings.length;
        let fetchedState = '';
        let fetchedCounty = '';

        // Dynamic fetch of Client/Prospect defaults on Add
        if (clNo !== undefined && prNo !== undefined) {
            const res = await get(`/v1.0/leases/recording-fees/default-location?clNo=${clNo}&prNo=${prNo}`);
            if (response.ok && res) {
                if (res.state) {
                    fetchedState = res.state;
                }
                if (res.county) {
                    fetchedCounty = res.county;
                }
            }
        }

        if (fetchedState) {
            fetchCountiesForState(fetchedState);
        }

        const newRecording: Partial<ILeaseRecording> = {
            liTg: invoice.liTg,
            operation: 'add',
            recordingStatus: 1,
            state: fetchedState,
            county: fetchedCounty,
        };
        setRecordings([...recordings, newRecording]);
        setIsDirty(true);
        setSaveStatus('idle');

        setExpandedIndices((prev) => new Set(prev).add(newIndex));
    };

    const handleRemoveRow = (index: number): void => {
        const updated = [...recordings];
        const recording = updated[index];

        if (recording.operation === 'add' && !recording.lrTg) {
            setRecordings(updated.filter((_, i) => i !== index));
            setIsDirty(true);
            setSaveStatus('idle');
            setInvalidRecordedDateIndices((prev) => {
                const next = new Set<number>();
                prev.forEach((i) => {
                    if (i < index) {
                        next.add(i);
                    }
                    if (i > index) {
                        next.add(i - 1);
                    }
                });
                return next;
            });
            setExpandedIndices((prev) => {
                const newSet = new Set<number>();
                prev.forEach((i) => {
                    if (i < index) {
                        newSet.add(i);
                    }
                    if (i > index) {
                        newSet.add(i - 1);
                    }
                });
                return newSet;
            });
        } else {
            updated[index] = { ...recording, operation: 'delete' };
            setRecordings(updated);
            setIsDirty(true);
            setSaveStatus('idle');
            setInvalidRecordedDateIndices((prev) => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
        }
    };

    const persistRecordings = async (closeAfterSave: boolean): Promise<boolean> => {
        if (isSaving) {
            return false;
        }
        if (!canEditRecordings) {
            if (closeAfterSave) {
                onClose();
            }
            return true;
        }
        if (invalidRecordedDateIndices.size > 0) {
            setExpandedIndices((prev) => new Set([...Array.from(prev), ...Array.from(invalidRecordedDateIndices)]));
            setSaveStatus('error');
            return false;
        }
        if (!isDirty) {
            if (closeAfterSave) {
                onClose();
            }
            return true;
        }

        setIsSaving(true);
        setSaveStatus('saving');
        try {
            const wasSaved = await onSave(recordings as ILeaseRecording[]);
            setSaveStatus(wasSaved ? 'saved' : 'error');
            if (wasSaved) {
                setIsDirty(false);
                if (closeAfterSave) {
                    onClose();
                }
            }
            return wasSaved;
        } catch {
            setSaveStatus('error');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async (): Promise<void> => {
        await persistRecordings(true);
    };

    const handleClose = async (): Promise<void> => {
        if (isUpdatingKudu || detailsIndex !== null) {
            return;
        }
        await persistRecordings(true);
    };

    const handleToggleRow = async (index: number): Promise<void> => {
        if (expandedIndices.has(index) && isDirty) {
            const wasSaved = await persistRecordings(false);
            if (!wasSaved) {
                return;
            }
        }
        toggleRow(index);
    };

    const activeRecordings = recordings.filter((r) => r.operation !== 'delete');
    const totalFees = activeRecordings.reduce((sum, rec) => sum + (Number(rec.recFee) || 0), 0);
    const hasKuduRecordingInformation = activeRecordings.some(
        (recording) => !!recording.lrTg && !!recording.book?.trim() && !!recording.page?.trim() && !!recording.recDate
    );

    const handleUpdateKuduRecording = async (): Promise<void> => {
        if (isUpdatingKudu || !leaseNo || !hasKuduRecordingInformation) {
            return;
        }

        setKuduUpdateResult(null);
        setIsUpdatingKudu(true);
        try {
            const wasSaved = await persistRecordings(false);
            if (!wasSaved) {
                setKuduUpdateResult({ success: false, message: 'Recording fees must be saved before KUDU can be updated.' });
                return;
            }

            const result = await post(`/v1.0/leases/master/${leaseNo}/update-kudu-recording`, {});
            const success = response.ok && result && result.status === 1;
            setKuduUpdateResult({
                success,
                message: success ? 'KUDU recording updated successfully.' : parseLeaseRecordsApiError(result || response.data, response.status).title,
            });
        } catch {
            setKuduUpdateResult({ success: false, message: 'KUDU recording update failed.' });
        } finally {
            setIsUpdatingKudu(false);
        }
    };

    const formatCurrency = (amount?: number): string => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    };

    return (
        <div style={styles.modalOverlay} onClick={() => void handleClose()}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>Recording Fees for Invoice #{invoiceNumber}</h3>
                    <div style={styles.totalFeesContainer}>
                        <span>Total Fees</span>
                        <div style={styles.totalFeesBox}>{formatCurrency(totalFees)}</div>
                    </div>
                </div>

                <LeaseRecordsErrorAlert error={error || null} />

                {/* Rows Container */}
                <div style={styles.gridBody}>
                    {recordings.map((rec, index) => {
                        if (rec.operation === 'delete') {
                            return null;
                        }

                        const isExpanded = expandedIndices.has(index);

                        // Determine available county options for this specific row based on its state
                        const countyOptions =
                            rec.state && countiesMap[rec.state]
                                ? [{ value: '', label: 'Select County...' }, ...countiesMap[rec.state]]
                                : [{ value: '', label: 'Select State First' }];

                        return (
                            <div key={rec.lrTg || `new-${index}`} style={styles.card}>
                                {/* Accordion Header */}
                                <div style={styles.cardHeader} onClick={() => void handleToggleRow(index)}>
                                    <div style={styles.headerInfo}>
                                        <div style={styles.headerField}>
                                            <span style={styles.headerLabel}>State</span>
                                            <span style={styles.headerValue}>{rec.state || '—'}</span>
                                        </div>
                                        <div style={styles.headerField}>
                                            <span style={styles.headerLabel}>County</span>
                                            <span style={styles.headerValue}>{rec.county || '—'}</span>
                                        </div>
                                        <div style={styles.headerField}>
                                            <span style={styles.headerLabel}>Rec. Date</span>
                                            <span style={styles.headerValue}>{rec.recDate ? new Date(rec.recDate).toLocaleDateString() : '—'}</span>
                                        </div>
                                        <div style={styles.headerField}>
                                            <span style={styles.headerLabel}>Rec. Fee</span>
                                            <span style={styles.headerValue}>{formatCurrency(rec.recFee)}</span>
                                        </div>
                                    </div>
                                    <div style={styles.headerActions}>
                                        <button
                                            type="button"
                                            style={styles.buttonSecondary}
                                            disabled={isSaving || isUpdatingKudu}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDetailsIndex(index);
                                            }}
                                        >
                                            Details ({(rec.recordingDetails || []).filter((detail) => detail.operation !== 'delete').length})
                                        </button>
                                        {canRemoveRecordings && (
                                            <button
                                                style={styles.removeButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveRow(index);
                                                }}
                                            >
                                                Remove
                                            </button>
                                        )}
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            style={{ ...styles.chevron, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                        >
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                    </div>
                                </div>

                                {/* Accordion Body */}
                                {isExpanded && (
                                    <div style={styles.cardBody}>
                                        <div style={styles.grid8Cols}>
                                            <SelectField
                                                label="State"
                                                value={rec.state || ''}
                                                options={statesList}
                                                editable={canEditRecordings}
                                                onChange={(v) => handleFieldChange(index, 'state', v)}
                                            />
                                            <SelectField
                                                label="County"
                                                value={rec.county || ''}
                                                options={countyOptions}
                                                editable={canEditRecordings && !!rec.state}
                                                onChange={(v) => handleFieldChange(index, 'county', v)}
                                            />
                                            <SelectField
                                                label="Inst. Type"
                                                value={rec.useBook || ''}
                                                options={INSTRUMENT_TYPE_OPTIONS}
                                                editable={canEditRecordings}
                                                onChange={(v) => handleFieldChange(index, 'useBook', v)}
                                            />
                                            <Field
                                                label="Book/Doc"
                                                value={rec.book || ''}
                                                editable={canEditRecordings}
                                                onChange={(v) => handleFieldChange(index, 'book', v)}
                                            />
                                            <Field
                                                label="Page"
                                                value={rec.page || ''}
                                                editable={canEditRecordings}
                                                onChange={(v) => handleFieldChange(index, 'page', v)}
                                            />
                                            <DateField
                                                label="Date Recorded"
                                                value={rec.recDate}
                                                editable={canEditRecordings}
                                                onChange={(v) => handleFieldChange(index, 'recDate', v)}
                                                onValidityChange={(isValid) => {
                                                    setInvalidRecordedDateIndices((prev) => {
                                                        const next = new Set(prev);
                                                        if (isValid) {
                                                            next.delete(index);
                                                        } else {
                                                            next.add(index);
                                                        }
                                                        return next;
                                                    });
                                                }}
                                            />
                                            <Field label="Invoice #" value={invoiceNumber} editable={false} />
                                            <Field label="Check #" type="number" value={rec.checkNo?.toString() || ''} editable={false} />

                                            {/* Bottom Row */}
                                            <SelectField
                                                label="Recording Status"
                                                value={rec.recordingStatus?.toString() || ''}
                                                options={RECORDING_STATUS_OPTIONS}
                                                editable={canEditRecordings}
                                                onChange={(v) => handleFieldChange(index, 'recordingStatus', v)}
                                            />
                                            <Field
                                                label="Recording Fee"
                                                type="number"
                                                prefix="$"
                                                value={rec.recFee?.toString() || ''}
                                                editable={canEditRecordingFinancials}
                                                onChange={(v) => handleFieldChange(index, 'recFee', v)}
                                            />
                                            <Field
                                                label="Assign Number"
                                                type="number"
                                                value={rec.assignNo?.toString() || ''}
                                                editable={canEditRecordings}
                                                onChange={(v) => handleFieldChange(index, 'assignNo', v)}
                                            />
                                            <Field
                                                label="Assign Date"
                                                value={formatDateForText(rec.assignDate)}
                                                editable={canEditRecordings}
                                                onChange={(v) => handleDateFieldChange(index, 'assignDate', v)}
                                            />
                                            <Field
                                                label="Transmit Number"
                                                type="number"
                                                value={rec.transmitNo?.toString() || ''}
                                                editable={canEditRecordings}
                                                onChange={(v) => handleFieldChange(index, 'transmitNo', v)}
                                            />
                                            <Field
                                                label="Transmit Date"
                                                value={formatDateForText(rec.tranDate)}
                                                editable={canEditRecordings}
                                                onChange={(v) => handleDateFieldChange(index, 'tranDate', v)}
                                            />
                                            <div />
                                            <div />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {detailsIndex !== null && recordings[detailsIndex] && (
                    <RecordingDetailsDialog
                        details={recordings[detailsIndex].recordingDetails || []}
                        editable={canEditRecordings}
                        onClose={() => setDetailsIndex(null)}
                        onApply={(details) => {
                            setRecordings((previous) =>
                                previous.map((recording, index) =>
                                    index === detailsIndex
                                        ? { ...recording, recordingDetails: details, operation: recording.operation === 'add' ? 'add' : 'update' }
                                        : recording
                                )
                            );
                            setIsDirty(true);
                            setSaveStatus('idle');
                            setDetailsIndex(null);
                        }}
                    />
                )}

                {/* Footer and Actions */}
                <div style={styles.modalFooter}>
                    {canEditRecordings && (
                        <button style={styles.buttonSecondary} onClick={handleAddRow} disabled={isUpdatingKudu}>
                            Add New Recording
                        </button>
                    )}
                    {hasKuduRecordingInformation && leaseNo && (
                        <button
                            style={{
                                ...styles.buttonSecondary,
                                opacity: isUpdatingKudu ? 0.6 : 1,
                                cursor: isUpdatingKudu ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() => void handleUpdateKuduRecording()}
                            disabled={isUpdatingKudu}
                        >
                            {isUpdatingKudu ? 'Updating KUDU...' : 'Update KUDU Recording'}
                        </button>
                    )}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem' }}>
                        {kuduUpdateResult && (
                            <span role="status" style={{ alignSelf: 'center', color: kuduUpdateResult.success ? '#15803D' : '#DC2626' }}>
                                {kuduUpdateResult.message}
                            </span>
                        )}
                        {saveStatus === 'saving' && <span style={{ alignSelf: 'center', color: '#2563EB' }}>Autosave pending...</span>}
                        {saveStatus === 'saved' && <span style={{ alignSelf: 'center', color: '#15803D' }}>Recording fees saved</span>}
                        {saveStatus === 'error' && (
                            <span role="alert" style={{ alignSelf: 'center', color: '#DC2626' }}>
                                Save failed; editor remains open
                            </span>
                        )}
                        <button style={styles.buttonSecondary} onClick={() => void handleClose()} disabled={isSaving || isUpdatingKudu}>
                            Close
                        </button>
                        {canEditRecordings && (
                            <button
                                style={{
                                    ...styles.buttonPrimary,
                                    opacity: isSaving || isUpdatingKudu || invalidRecordedDateIndices.size > 0 ? 0.6 : 1,
                                    cursor: isSaving || isUpdatingKudu || invalidRecordedDateIndices.size > 0 ? 'not-allowed' : 'pointer',
                                }}
                                onClick={handleSave}
                                disabled={isSaving || isUpdatingKudu || invalidRecordedDateIndices.size > 0}
                            >
                                {isSaving ? 'Saving...' : 'Save Recordings'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Component Styles ---
const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(3px)',
        padding: '1rem',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        padding: '1.25rem',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '1400px',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        position: 'relative',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: '0.75rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#111827',
        margin: 0,
    },
    gridBody: {
        maxHeight: '70vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        padding: '0.15rem',
    },
    card: {
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        transition: 'box-shadow 0.2s ease',
        backgroundColor: '#FFFFFF',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.5rem 1rem',
        cursor: 'pointer',
        backgroundColor: '#F9FAFB',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
    },
    headerInfo: {
        display: 'flex',
        gap: '3rem',
        alignItems: 'center',
    },
    headerField: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: '100px',
    },
    headerLabel: {
        fontSize: '0.75rem',
        color: '#6B7280',
        fontWeight: 500,
        textTransform: 'uppercase',
    },
    headerValue: {
        fontSize: '1rem',
        color: '#111827',
        fontWeight: 600,
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
    },
    chevron: {
        transition: 'transform 0.2s ease',
        color: '#6B7280',
    },
    cardBody: {
        padding: '0.75rem 1rem',
        borderTop: '1px solid #E5E7EB',
    },
    grid8Cols: {
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gap: '0.5rem 0.75rem',
        alignItems: 'end',
    },
    modalFooter: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginTop: '0.5rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid #E5E7EB',
    },
    buttonPrimary: {
        padding: '0.5rem 1.25rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#2563EB',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    buttonSecondary: {
        padding: '0.5rem 1.25rem',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    removeButton: {
        padding: '0.25rem 0.75rem',
        border: '1px solid #EF4444',
        borderRadius: '6px',
        backgroundColor: '#FEF2F2',
        color: '#EF4444',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.75rem',
    },
    totalFeesContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        fontSize: '0.875rem',
        color: '#374151',
    },
    totalFeesBox: {
        padding: '0.4rem 0.75rem',
        border: '1px solid #D1D5DB',
        backgroundColor: '#F9FAFB',
        fontWeight: 600,
        borderRadius: '6px',
        minWidth: '100px',
        textAlign: 'right',
    },
};

export default RecordingsModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/RecordingsModal.tsx